import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Clock3, Trash2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { useLanguage } from '../../contexts/LanguageContext';

const CLEAR_OPTIONS = [
  { period: 86400, label: 'Pas 24 orësh' },
  { period: 604800, label: 'Pas 7 ditësh' },
  { period: 2592000, label: 'Pas 1 muaji' },
  { period: null, label: 'Të gjitha' },
];

const ClearConversationDialog = ({ open, onClose, onClear, isNotes = false }) => {
  const { t } = useLanguage();
  const [isClearing, setIsClearing] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const handleClear = async (period) => {
    setIsClearing(true);
    setError('');
    try {
      const succeeded = await onClear(period);
      if (succeeded === false) {
        setError(t('Biseda nuk u pastrua. Provoni përsëri.'));
        return;
      }
      onClose();
    } catch (clearError) {
      console.error('Unable to clear chat messages:', clearError);
      setError(t('Biseda nuk u pastrua. Provoni përsëri.'));
    } finally {
      setIsClearing(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex min-h-screen min-w-full items-center justify-center bg-zinc-950/50 p-4 backdrop-blur-sm"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isClearing) onClose();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="clear-conversation-title"
        aria-describedby="clear-conversation-description"
        className="w-full max-w-md overflow-hidden rounded-2xl border-0 bg-white text-zinc-950 shadow-2xl"
      >
        <header className="flex items-start justify-between gap-3 border-b border-zinc-100 p-5">
          <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-700">
            <Clock3 size={18} />
          </span>
          <div>
            <h2 id="clear-conversation-title" className="text-sm font-bold text-zinc-950">
              {t(isNotes ? 'Fshi shënimet e mia' : 'Fshi bisedën vetëm për mua')}
            </h2>
            <p id="clear-conversation-description" className="mt-1 text-xs leading-relaxed text-zinc-600">
              {isNotes
                ? t('Shënimet tuaja personale fshihen menjëherë.')
                : t('Zgjidhni afatin. Mesazhet fshihen vetëm nga llogaria juaj.')}
            </p>
          </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isClearing}
            className="shrink-0 rounded-lg p-1.5 text-zinc-400 transition hover:bg-zinc-100 hover:text-zinc-900 disabled:opacity-50"
            aria-label={t('Mbyll dritaren')}
            title={t('Mbyll')}
          >
            <X size={18} />
          </button>
        </header>

        <div className="space-y-2 p-5">
          {CLEAR_OPTIONS.filter(({ period }) => !isNotes || period === null).map(({ period, label }) => (
            <button
              key={label}
              type="button"
              disabled={isClearing}
              onClick={() => handleClear(period)}
              className="flex w-full items-center justify-between rounded-xl border border-zinc-200 bg-white px-3.5 py-3 text-left transition hover:border-amber-300 hover:bg-amber-50/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span>
                <span className="block text-xs font-semibold text-zinc-950">{t(label)}</span>
                <span className="mt-0.5 block text-[11px] text-zinc-600">
                  {period === null
                    ? isNotes
                      ? t('Hiqi menjëherë të gjitha shënimet nga llogaria jote')
                      : t('Hiqi menjëherë dhe mbaji të fshehura vetëm në llogarinë tënde. Pjesëmarrësit e tjerë do t’i shohin ende.')
                    : t('Fshihet vetëm nga llogaria juaj pas afatit')}
                </span>
              </span>
              {period === null
                ? <Trash2 size={16} className="shrink-0 text-red-600" />
                : <Clock3 size={16} className="shrink-0 text-amber-700" />}
            </button>
          ))}
          {error && <p role="alert" className="text-xs text-red-600">{error}</p>}
        </div>

        <footer className="flex justify-end border-t border-zinc-100 bg-zinc-50 px-5 py-3">
          <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={isClearing} className="rounded-xl text-xs text-zinc-700 hover:bg-zinc-200">
            {t('Anulo')}
          </Button>
        </footer>
      </section>
    </div>,
    document.body
  );
};

export default ClearConversationDialog;
